import { useSnackbar } from '@mui/base';
import { useDialog } from 'contexts/dialog';
import { omit } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Paper,
    ResponsiveDialog,
    RichTextArea,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';

const defaultErrors = {
    gradingCustomContext: '',
    criteria: {},
};

const EditGradingSettingsDialog = memo(({ open, form: data, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');
    const { message } = useSnackbar();

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState(defaultErrors);

    const handleClose = () => {
        if (form.isDirty) {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit now, any changes you have made to grading settings will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    onClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            onClose();
        }
    };

    const handleClickDelete = (criterion) => {
        setDialog({
            open: true,
            message: t('dialog.delete-grading-instructions.title', 'Delete Grading Instructions for This Criterion?'),
            description: t(
                'dialog.delete-grading-instructions.description',
                'This will permanently delete any existing grading instructions. You will still be able to add new instructions.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({
                    ...current,
                    criteria: {
                        ...current.criteria,
                        [criterion.id]: {
                            grading_instructions: null,
                        },
                    },
                }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateAll = () => {
        let isValid = true;

        setErrors({
            gradingCustomContext: getHTMLWordCount(form.gradingCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT,
            criteria: Object.entries(form.criteria).reduce((acc, [criterionId, { grading_instructions }]) => {
                return {
                    ...acc,
                    [criterionId]: getHTMLWordCount(grading_instructions || '') > CUSTOM_CONTENT_WORD_COUNT_LIMIT,
                };
            }, {}),
        });

        if (
            getHTMLWordCount(form.gradingCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT ||
            Object.values(form.criteria)
                .map(({ grading_instructions }) => grading_instructions)
                .some(
                    (gradingInstructions) =>
                        getHTMLWordCount(gradingInstructions || '') > CUSTOM_CONTENT_WORD_COUNT_LIMIT
                )
        ) {
            isValid = false;
            message.error(
                t(
                    'error.instructions-too-long',
                    "Please make sure the instructions don't exceed {{wordCount}} words.",
                    { wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                )
            );
        }

        return isValid;
    };

    const handleClickSave = () => {
        const isValid = validateAll();
        if (!isValid) return;
        onSave(omit(form, ['isDirty']));
        onClose();
    };

    useEffect(() => {
        if (open) {
            setForm({
                gradingCustomContext: data.gradingCustomContext,
                criteria:
                    data.rubric?.criteria?.reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.id]: {
                                grading_instructions:
                                    data.gradingSettings?.criteria?.[cur.id]?.grading_instructions || null,
                            },
                        };
                    }, {}) || {},
                isDirty: false,
            });
            setErrors({
                gradingCustomContext: '',
                criteria: {},
            });
        } else {
            setTimeout(() => {
                setForm(null);
                setErrors(defaultErrors);
            }, 150);
        }
    }, [data.gradingCustomContext, data.gradingSettings, data.rubric?.criteria, open]);

    if (!form) return null;

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('advanced-grading.title', 'Advanced Grading Settings')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={2}>
                    <Text variant={'p1'} weight={'medium'}>
                        {t(
                            'feedback-settings.additional-instruction.grading-label',
                            'Additional Grading Instructions for the AI'
                        )}
                    </Text>
                    <CustomPaper variant={'outlined'}>
                        <Stack spacing={5}>
                            {data.rubric && (
                                <Text>
                                    {t(
                                        'advanced-grading.description',
                                        'In this section, you can give the AI extra guidance for its grading suggestions. These instructions can be general or tailored to each specific criterion.'
                                    )}
                                </Text>
                            )}
                            <RichTextArea
                                error={Boolean(errors.gradingCustomContext)}
                                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                                    wordLimitStatus: `${getHTMLWordCount(
                                        form.gradingCustomContext
                                    )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                })}
                                helperTextPosition={'end'}
                                label={t('advanced-grading.general-instructions', 'General Instructions')}
                                placeholder={t(
                                    'advanced-grading.grading-suggestions-placeholder',
                                    'Do you have any additional instructions about grading suggestions?'
                                )}
                                value={form.gradingCustomContext}
                                onChange={(value) =>
                                    setForm((current) => ({ ...current, gradingCustomContext: value }))
                                }
                            />
                            <Stack spacing={2}>
                                <Text>
                                    {t('advanced-grading.criterion-instructions', 'Criterion-Specific Instructions')}
                                </Text>
                                <Stack spacing={4}>
                                    {!data.rubric ? (
                                        <Text color={'light'}>
                                            {t(
                                                'advanced-grading.criterion-instruction-missing-rubric',
                                                'Please create a rubric in “General Assignment Details” to enable criterion-specific grading instructions.'
                                            )}
                                        </Text>
                                    ) : (
                                        data.rubric.criteria.map((criterion) => (
                                            <CriterionPaper key={criterion.id} variant={'outlined'}>
                                                <Stack spacing={4}>
                                                    <Stack spacing={2}>
                                                        <Text>{criterion.title}</Text>
                                                        <Text color={'light'}>{criterion.description}</Text>
                                                    </Stack>
                                                    {form.criteria[criterion.id].grading_instructions === null ? (
                                                        <Button
                                                            sx={{ alignSelf: 'flex-start' }}
                                                            startIcon={<Icon kind={'add'} />}
                                                            variant={'borderless'}
                                                            onClick={() =>
                                                                setForm((current) => ({
                                                                    ...current,
                                                                    criteria: {
                                                                        ...current.criteria,
                                                                        [criterion.id]: {
                                                                            grading_instructions: '',
                                                                        },
                                                                    },
                                                                }))
                                                            }
                                                        >
                                                            {t('advanced-grading.add-instructions', 'Add Instructions')}
                                                        </Button>
                                                    ) : (
                                                        <Stack spacing={2}>
                                                            <RichTextArea
                                                                error={Boolean(errors.criteria[criterion.id])}
                                                                helperText={t(
                                                                    'remaining-word-count',
                                                                    '{{wordLimitStatus}} words',
                                                                    {
                                                                        wordLimitStatus: `${getHTMLWordCount(
                                                                            form.criteria[criterion.id]
                                                                                .grading_instructions
                                                                        )}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                                                                    }
                                                                )}
                                                                helperTextPosition={'end'}
                                                                label={t(
                                                                    'advanced-grading.grading-instructions-label',
                                                                    'Grading Instructions'
                                                                )}
                                                                placeholder={t(
                                                                    'advanced-grading.grading-instructions-placeholder',
                                                                    'Enter additional grading instructions for this criterion'
                                                                )}
                                                                value={form.criteria[criterion.id].grading_instructions}
                                                                onChange={(html) =>
                                                                    setForm((current) => ({
                                                                        ...current,
                                                                        criteria: {
                                                                            ...current.criteria,
                                                                            [criterion.id]: {
                                                                                grading_instructions: html,
                                                                            },
                                                                        },
                                                                    }))
                                                                }
                                                            />
                                                            <IconButton
                                                                sx={{ alignSelf: 'flex-end' }}
                                                                aria-label={`remove grading instructions for ${criterion.title}`}
                                                                kind={'delete'}
                                                                size={'small'}
                                                                variant={'square'}
                                                                onClick={() => handleClickDelete(criterion)}
                                                            />
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </CriterionPaper>
                                        ))
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>
                    </CustomPaper>
                </Stack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} spacing={4}>
                    <Button aria-hidden={false} color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleClickSave}>{t('common:controls.save-settings', 'Save Settings')}</Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            maxWidth: '960px',
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        maxWidth: '700px',
        padding: theme.spacing(5),
    };
});

const CriterionPaper = styled(Paper)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

export default EditGradingSettingsDialog;
